'use client';

import { useContext, useEffect, useState } from 'react';
import { BellRing, X } from 'lucide-react';
import { toast } from 'react-toastify';
import * as Dialog from '@radix-ui/react-dialog';
import { useTranslations } from 'next-intl';
import AuthContext from '../../context/AuthContext';
import { useSendrealmPush } from '../../context/SendrealmPushContext';
import Loading from '../Loading';
export default function WebPushPermissionPrompt() {
  const t = useTranslations();
  const auth = useContext(AuthContext);
  const webPush = useSendrealmPush();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const userId = auth.userData?._id;
  const canOfferNotifications = !auth.isLoading && auth.isLoggedIn && Boolean(userId && auth.userData?.email) && webPush.initialized && !webPush.loading && !webPush.subscribed && webPush.permissionStatus !== 'denied' && webPush.permissionStatus !== 'unsupported';
  useEffect(() => {
    if (!canOfferNotifications || !userId) {
      setOpen(false);
      return;
    }
    setOpen(true);
  }, [canOfferNotifications, userId]);
  function dismiss() {
    setOpen(false);
  }
  async function enableNotifications() {
    if (!userId) return;
    setSubmitting(true);
    try {
      await webPush.enable();
      setOpen(false);
      toast(t('common.web_push_prompt.enabled_message'), {
        position: 'bottom-center',
        type: 'success'
      });
    } catch {
      toast(t('common.web_push_prompt.error_message'), {
        position: 'bottom-center',
        type: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  }
  return <Dialog.Root open={open} onOpenChange={nextOpen => {
    if (!nextOpen) dismiss();
  }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/75" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-md border border-white/10 bg-gray-600 p-5 text-white shadow-2xl shadow-black/60 focus:outline-none">
          <div className="flex items-start justify-between gap-4">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-red-600/15 text-red-500">
              <BellRing size={23} />
            </span>
            <Dialog.Close asChild>
              <button type="button" className="rounded-md p-1 text-gray-100 transition hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/30" aria-label={t('common.web_push_prompt.close_label')}>
                <X size={21} />
              </button>
            </Dialog.Close>
          </div>

          <Dialog.Title className="mt-4 text-xl font-bold leading-7">
            {t('common.web_push_prompt.title')}
          </Dialog.Title>
          <Dialog.Description className="mt-2 text-sm leading-6 text-gray-100">
            {t('common.web_push_prompt.description')}
          </Dialog.Description>

          <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button type="button" className="inline-flex min-h-[44px] items-center justify-center rounded-md border border-white/15 px-4 text-sm font-bold text-white transition hover:border-white/30 hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-white/30 disabled:cursor-not-allowed disabled:opacity-60" disabled={submitting} onClick={dismiss}>
              {t('common.web_push_prompt.not_now_button')}
            </button>
            <button type="button" className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-md bg-red-600 px-4 text-sm font-bold text-white transition hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-red-600/60 focus:ring-offset-2 focus:ring-offset-gray-600 disabled:cursor-not-allowed disabled:opacity-60" disabled={submitting} onClick={() => void enableNotifications()}>
              {submitting && <Loading size={17} />}
              {t('common.web_push_prompt.enable_button')}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>;
}